/**
 * 日期、事件相关工具方法
 */

import React from 'react';
import moment from 'moment';
import { isFunction } from 'lodash-es';

import type { SelectOption } from 'tea-component';
import type { IPlaceholder, IDateFormat } from '@home/types/common';
import type { IGetDaysDataOfCalendarOptions, IHolidaysCalendarCell, IMonthStatus } from '@home/types/utils';

/**
 * 日期格式化
 * @param dateStr 日期字符串
 * @param format moment的日期格式化令牌
 * @param placeholder 占位符
 */
export const formatDateStr = (
  dateStr?: string,
  format = 'YYYY-MM-DD',
  placeholder: IPlaceholder = '',
): string | React.ReactElement => {
  if (!dateStr) return placeholder;
  const dateM = moment(dateStr);
  if (!dateM.isValid()) return placeholder;

  try {
    return dateM.format(format);
  } catch (exception) {
    return placeholder;
  }
};

/**
 * 日期格式化
 * @param dateStr 日期字符串
 * @param format moment的日期格式化令牌 或 日期格式化方法
 * @param placeholder 占位符
 */
export const formatDateStrPlus = (
  dateStr?: string,
  format?: IDateFormat,
  placeholder?: IPlaceholder,
): string | React.ReactElement => {
  if (isFunction(format)) return format(dateStr);
  return formatDateStr(dateStr, format, placeholder);
};

/**
 * 获取指定日期 所属月份 总共有多少天
 * @param date 指定日期
 * @return 天数（指定日期所属月份 总共有多少天）
 */
const getDaysOfMonth = (date: string | moment.Moment): number => {
  const m = moment.isMoment(date) ? date : moment(date);
  return m.isValid() ? m.daysInMonth() : 0;
};

/**
 * 获取节假日日历的天数据(日历单元格数据)
 * @param baseDate 代表当前日期,需要通过当前日期 确定年份和月份
 * @param days 天数,需要在当前月份中 截取的 天数
 * @param from 截取开始的方向,begin 从第一天开始往后截取,end 从当月最后一天开始往前截取
 * @param monthStatus
 * @returns 节假日日历的天数据(日历单元格数据)
 */
const getHolidaysCalendarCell = (
  baseDate: moment.Moment,
  days: number,
  from: 'begin' | 'end',
  monthStatus: IMonthStatus,
): IHolidaysCalendarCell[] => {
  // noinspection SuspiciousTypeOfGuard
  if (typeof days !== 'number') return [];

  const daysInMonth = baseDate.daysInMonth();
  return Array.from({ length: days }).map((_d, i) => {
    const base = i + 1;
    const dateNum = from === 'begin' ? base : daysInMonth - (days - base);
    const date = moment(baseDate).date(dateNum);

    return {
      date: date.format('YYYY-MM-DD'),
      label: date.date(),
      monthStatus: {
        isCurrentMonth: monthStatus?.isCurrentMonth || false,
        isPrevMonth: monthStatus?.isPrevMonth || false,
        isNextMonth: monthStatus?.isNextMonth || false,
      },
    };
  });
};

/**
 * 获取节假日日历天数据(日历单元格数据),格式为二维格式 每7天的数据 作为 一组数据
 * @param cells 节假日日历天数据(日历单元格数据) 一维格式数据
 * @param interval 间隔,7天作为一周的天数据,所以间隔就是7
 * @returns 节假日日历天数据(日历单元格数据) 二维格式数据
 */
const get2DimensionHolidaysCalendarCells = (
  cells: IHolidaysCalendarCell[],
  interval = 7,
): IHolidaysCalendarCell[][] => {
  if (!Array.isArray(cells)) return [];
  const result: IHolidaysCalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += interval) {
    const cell = cells[i];
    if (!cell) continue;
    const temp = cells.slice(i, i + interval);
    result.push(temp);
  }
  return result;
};

/**
 * 获取指定日期 所属月份 的 日历天数数据，可指定返回数据格式 是 一维数组 还是 二维数组
 * @param date 指定日期
 * @param options 配置对象，具体配置细节 参考 GetDaysDataOfCalendarOptions的类型定义
 * @return 日历天 数据，若指定生成一维数组格式 则按照 一维数组格式返回，若指定生成二维数组格式 则按照 二维数组格式返回
 */
export const getCalendarCellsData = (
  date: string | moment.Moment,
  options?: IGetDaysDataOfCalendarOptions,
): IHolidaysCalendarCell[] | IHolidaysCalendarCell[][] => {
  const { type } = options || {};
  const m = moment(date);
  // 当月的天数（当月有多少天）
  const days = getDaysOfMonth(m);
  // 当月第一天
  const firstDay = m.startOf('month');
  // 当月第一天是星期几
  const weekDay = firstDay.day();
  // 当月日历的天数 前面 需要补充多少天（日历天数 总共42天，需要在当月天数 前后 分别补充额外的天数）
  const frontDays = weekDay !== 0 ? weekDay - 1 : 6; // 星期天是0，星期一到星期六 是 1到6
  // 当月日历的天数 后面 需要补充多少天
  const backDays = 42 - (frontDays + days);
  // 上一个月
  const prevMonth = moment(m).subtract(1, 'month');
  // 下一个月
  const nextMonth = moment(m).add(1, 'month');

  const frontDaysData = getHolidaysCalendarCell(prevMonth, frontDays, 'end', {
    isPrevMonth: true,
  });
  const daysData = getHolidaysCalendarCell(m, days, 'begin', {
    isCurrentMonth: true,
  });
  const backDaysData = getHolidaysCalendarCell(nextMonth, backDays, 'begin', {
    isNextMonth: true,
  });

  const calendarCells = [...frontDaysData, ...daysData, ...backDaysData];

  // 按照二维数组格式返回日历天数据(日历单元格数据)
  if (type === '2') {
    return get2DimensionHolidaysCalendarCells(calendarCells);
  }

  // 按照一维数组格式返回日历天数据(日历单元格数据)
  return calendarCells;
};

/**
 * 获取指定当前年份的年份列表数据
 * @param year 当前年份
 * @returns 年份列表数据
 */
export const getYearsOptions = (year: string | number): SelectOption[] => {
  // 年份
  const years: SelectOption[] = [];
  for (let i = 0; i < Number(year) - 2014; i++) {
    const obj: SelectOption = {
      value: `${i + 2020}`,
      text: `${i + 2020}`,
    };
    years.push(obj);
  }
  return years;
};

const MONTHS_INTL_KEYS = [
  'dbh_january',
  'dbh_february',
  'dbh_march',
  'dbh_april',
  'dbh_may',
  'dbh_june',
  'dbh_july',
  'dbh_august',
  'dbh_september',
  'dbh_october',
  'dbh_november',
  'dbh_december',
];
/**
 * 获取月份列表数据
 * @param formatMessage 国家化语言转换函数
 * @returns 月份列表数据，1月的value对应1，12月的value对应12
 */
export const getMonthsOptions = (formatMessage: Function) => {
  // 月份
  const months: SelectOption[] = [];
  for (let i = 0; i < 12; i++) {
    const monthStr = `${i + 1}`.padStart(2, '0');
    const obj: SelectOption = {
      value: monthStr,
      text: formatMessage?.({ id: MONTHS_INTL_KEYS[i], defaultMessage: i + 1 }),
    };
    months.push(obj);
  }
  return months;
};

const WEEKS_INTL_OPTIONS = [
  {
    id: 'dbh_sunday',
    defaultMessage: '日',
  },
  {
    id: 'dbh_monday',
    defaultMessage: '一',
  },
  {
    id: 'dbh_tuesday',
    defaultMessage: '二',
  },
  {
    id: 'dbh_wednesday',
    defaultMessage: '三',
  },
  {
    id: 'dbh_thursday',
    defaultMessage: '四',
  },
  {
    id: 'dbh_friday',
    defaultMessage: '五',
  },
  {
    id: 'dbh_saturday',
    defaultMessage: '六',
  },
];

const NORMAL_WEEKS_INTL_OPTIONS: { id: string; defaultMessage?: string }[] = [
  { id: 'dbh_week_sunday' },
  { id: 'dbh_week_monday' },
  { id: 'dbh_week_tuesday' },
  { id: 'dbh_week_wednesday' },
  { id: 'dbh_week_thursday' },
  { id: 'dbh_week_friday' },
  { id: 'dbh_week_saturday' },
];
/**
 * 获取周数据
 * @param formatMessage 国家化语言转换方法
 * @returns 周数据
 */
export const getWeeksOptions = (formatMessage: Function) => {
  // 周
  const weeks = Array.from({ length: 7 }).map((_d, i: number) => {
    const weekIntlOption = WEEKS_INTL_OPTIONS?.[i] || {};
    return {
      text: formatMessage?.({
        id: weekIntlOption?.id,
        defaultMessage: weekIntlOption?.defaultMessage,
      }),
      value: i,
    };
  });

  return [...weeks?.slice(1), weeks?.[0]];
};

// 获取国家化处理的周
export const getWeekLocaleText = (formatMessage: Function, week: number): string => {
  const weekIntlOption = NORMAL_WEEKS_INTL_OPTIONS?.[week];
  if (!weekIntlOption) return '';
  return formatMessage?.({
    id: weekIntlOption?.id,
    defaultMessage: weekIntlOption?.defaultMessage,
  });
};

/**
 * 设置moment的locale配置的中week的配置，一般用来设置星期几作为一周的开始日期
 * @param cfg 配置
 * @return 恢复的方法（临时设置，实现业务功能后需要恢复到之前的配置）
 */
export const setLocaleWeekCfgOfMoment = (cfg: { dow?: number; doy?: number }) => {
  const { dow: dowCfg, doy: doyCfg } = cfg || {};

  const momentLocale = moment.locale();
  // @ts-ignore
  const currentMomentLocaleWeek = moment.localeData()?._week || {};
  const { dow, doy } = currentMomentLocaleWeek || {};
  // 临时设置星期一作为一周的开始日期
  moment.updateLocale(momentLocale, {
    week: {
      // 星期的第一天是星期一
      dow: dowCfg ?? dow,
      // 年份的第一周必须包含1月4日 (7 + 1 - 4)
      doy: doyCfg ?? doy,
    },
  });

  // 恢复临时设置前的配置
  return () => {
    moment.updateLocale(momentLocale, {
      week: {
        dow,
        doy,
      },
    });
  };
};
